import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

type Counts = Record<string, number>;

function CountList({ counts, testId }: { counts: Counts; testId: string }) {
  const rows = Object.entries(counts);
  if (!rows.length) return <p className="mt-2 text-sm text-slate-500">Nada.</p>;
  return (
    <ul className="mt-2 space-y-1 font-mono text-xs text-slate-600" data-testid={testId}>
      {rows.map(([k, v]) => <li key={k} className="flex justify-between gap-4"><span>{k}</span><span>{v}</span></li>)}
    </ul>
  );
}

export function ProdResetCard() {
  const [plan, setPlan] = useState<{ enabled: boolean; counts: Counts } | null>(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ deleted: Counts; final: Counts } | null>(null);
  useEffect(() => { api.get("/admin/prod-reset").then((r) => setPlan(r.data)).catch(() => setPlan(null)); }, []);
  if (!plan?.enabled) return null;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/prod-reset", { confirm });
      setResult(data);
      toast.success("Producción preparada");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#B23A1E]/40 bg-white p-5 shadow-sm sm:p-7" data-testid="prod-reset-card">
      <h3 className="font-display text-base font-bold text-[#B23A1E]">Preparar producción</h3>
      {result ? (
        <>
          <p className="mt-2 text-sm text-slate-600" data-testid="prod-reset-done">Listo. Tu sesión se cerró; entra con "¿Olvidaste tu contraseña?". Conteo final:</p>
          <CountList counts={result.final} testId="prod-reset-final" />
        </>
      ) : (
        <>
          <p className="mt-1 text-sm text-slate-500">Borra todos los usuarios excepto el tuyo y todos los datos. Conserva la configuración de /admin. Se borrará:</p>
          <CountList counts={plan.counts} testId="prod-reset-plan" />
          <Input value={confirm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)} placeholder='Escribe "BORRAR"'
            className="mt-4 h-10" data-testid="prod-reset-confirm-input" />
          <Button onClick={run} disabled={busy || confirm !== "BORRAR"} className="mt-3 w-full bg-[#B23A1E] text-white hover:bg-[#9a3119]" data-testid="prod-reset-run">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preparar producción"}
          </Button>
        </>
      )}
    </div>
  );
}
